import * as React from "react";
import { FILTER_SEPARATOR } from "@/constants";
import { Box, List, ListItem, ListItemButton, ListItemText, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import { List as ListIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";

import { FilterButton, FilterPopover, useFilterContext } from "@/components/core/filter-button";

import { ChipsAutocomplete } from "../chips-autocomplete";
import { ChipsFreeSolo } from "../chips-free-solo";
import { Dropdown } from "../core/dropdown/dropdown";
import { DropdownContext } from "../core/dropdown/dropdown-context";
import { DropdownPopover } from "../core/dropdown/dropdown-popover";
import { DropdownTrigger } from "../core/dropdown/dropdown-trigger";
import { CustomDatePicker } from "../customDatePicker/custom-date-picker";
import { CustomTimePicker } from "../customTimePicker/custom-time-picker";
import { FieldType } from "../dynamicField/types";
import { FilterFetchMultiSelect } from "../fetch-multi-select";
import { FilterFetchSelect } from "../fetch-select";
import { FilterSelect } from "../select";
import { AdvancedFilterButton } from "./advanced-filter-button";
import { FiltersDrawer } from "./filters-drawer";
import {
	FilterCondition,
	FilterDrawerOption,
	FilterOption,
	Filters,
	FiltersDrawer as FiltersDrawerType,
	FilterType,
	Option,
} from "./types";

export interface TableToolbarProps {
	tableHeaderTitle?: string;
	filters?: Filters;
	setFilters: (filters: Filters | ((prev: Filters) => Filters)) => void;
	resetFilters: () => void;
	renderHeaderButtons?: () => React.ReactNode;
	selectedRowActions?: any[];
	selectedRows?: string[];
	isAllDataSelected?: boolean;
	totalItems?: number;
	drawerFilters?: FiltersDrawerType;
	setDrawerFilters: (filters: FiltersDrawerType | ((prev: FiltersDrawerType) => FiltersDrawerType)) => void;
	filtersDrawerOptions?: FilterDrawerOption[];
	isFilterDrawerDisabled?: boolean;
	// Advanced filters
	availableFields?: FieldType[];
	conditions: FilterCondition[];
	setConditions: (conditions: FilterCondition[]) => void;
	resetAllDataSelected: () => void;
}

export function TableToolbar({
	tableHeaderTitle,
	filters = {},
	setFilters,
	resetFilters,
	renderHeaderButtons,
	selectedRowActions,
	selectedRows = [],
	isAllDataSelected = false,
	totalItems = 0,
	drawerFilters = {},
	setDrawerFilters,
	filtersDrawerOptions = [],
	isFilterDrawerDisabled,
	availableFields = [],
	conditions = [],
	setConditions,
	resetAllDataSelected,
}: TableToolbarProps): React.JSX.Element {
	const filtersOptions = Object.values(filters);
	const [searchParams, setSearchParams] = useSearchParams();

	const handleFilterChange = (
		name: string,
		value: string | Option[] | Option | { from?: string; to?: string },
		type: FilterType
	) => {
		resetAllDataSelected();
		setFilters((prev) => {
			const newFilters = { ...prev, [name]: { ...prev[name], value } };

			if (type === FilterType.ATTEMPTS) {
				const attemptsValue = value as { from?: string; to?: string };
				searchParams.delete(`${name}From`);
				searchParams.delete(`${name}To`);
				if (attemptsValue?.from) {
					searchParams.set(`${name}From`, attemptsValue.from);
				}
				if (attemptsValue?.to) {
					searchParams.set(`${name}To`, attemptsValue.to);
				}
			} else {
				if (searchParams.get(name)) {
					searchParams.delete(name);
				}
				if (value) {
					if (Array.isArray(value)) {
						for (const v of value) {
							searchParams.append(name, `${(v as Option).label}${FILTER_SEPARATOR}${(v as Option).value}`);
						}
					} else {
						if ((type === FilterType.FETCH_SELECT || type === FilterType.SELECT) && typeof value !== "string") {
							searchParams.append(name, `${(value as Option).label}${FILTER_SEPARATOR}${(value as Option).value}`);
						} else {
							searchParams.append(name, value as string);
						}
					}
				}
			}

			setSearchParams(searchParams);

			return newFilters;
		});
	};

	const handleClearFilters = () => {
		for (const filter of filtersOptions) {
			if (filter.type === FilterType.ATTEMPTS) {
				searchParams.delete(`${filter.name}From`);
				searchParams.delete(`${filter.name}To`);
			} else if (searchParams.get(filter.name)) {
				searchParams.delete(filter.name);
			}
		}
		setSearchParams(searchParams);

		resetFilters();
	};

	const hasFilters = filtersOptions.some((filter) => {
		if (filter.type === FilterType.ATTEMPTS) {
			const attemptsValue = filter.value as { from?: string; to?: string } | undefined;
			return attemptsValue?.from || attemptsValue?.to;
		}
		if (
			filter.type === FilterType.MULTI_SELECT ||
			filter.type === FilterType.MULTI_SELECT_FREE_SOLO ||
			filter.type === FilterType.FETCH_MULTI_SELECT
		) {
			return (filter.value as Option[])?.length > 0;
		}
		if (filter.type === FilterType.FETCH_SELECT || filter.type === FilterType.SELECT) {
			return (filter.value as Option)?.value;
		}
		return filter.value;
	});

	return (
		<Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap", px: 3, py: 2, minHeight: "72px" }}>
			{tableHeaderTitle && (
				<Typography variant="h6" color="text.primary" fontWeight={500}>
					{tableHeaderTitle}
				</Typography>
			)}
			<Stack direction="row" spacing={2} sx={{ alignItems: "center", flex: "1 1 auto", flexWrap: "wrap" }}>
				<Stack direction="row" spacing={2} sx={{ alignItems: "center", flex: "1 1 auto", flexWrap: "wrap" }}>
					{filtersOptions.map((filter) => {
						const { type, value, label, name } = filter;

						const displayValue = (() => {
							if (!value) return value;

							if (type === FilterType.TIMESTAMP) return dayjs(+value).format("DD/MM/YYYY HH:mm");

							if (type === FilterType.ATTEMPTS) {
								const attemptsValue = value as { from?: string; to?: string };

								const chips: Option[] = [];
								if (attemptsValue?.from) {
									chips.push({ label: `From: ${attemptsValue.from}`, value: attemptsValue.from });
								}
								if (attemptsValue?.to) {
									chips.push({ label: `To: ${attemptsValue.to}`, value: attemptsValue.to });
								}
								return chips;
							}

							if (
								type === FilterType.MULTI_SELECT ||
								type === FilterType.MULTI_SELECT_FREE_SOLO ||
								type === FilterType.FETCH_MULTI_SELECT
							) {
								return value as Option[];
							}

							if (type === FilterType.FETCH_SELECT || type === FilterType.SELECT) {
								return (value as Option)?.label;
							}

							return typeof value === "string" ? value : "";
						})();

						return (
							<FilterButton
								displayValue={displayValue}
								label={label}
								key={name}
								onFilterApply={(value) => {
									handleFilterChange(name, value as string, type);
								}}
								onFilterDelete={() => {
									handleFilterChange(name, "", type);
								}}
								popover={
									<TextPopover
										title={`${label}`}
										type={type}
										filterName={name}
										options={filter.options}
										filter={filter}
									/>
								}
								value={value}
								onChipDelete={(chipValue) => {
									if (type === FilterType.ATTEMPTS) {
										const attemptsValue = value as { from?: string; to?: string };
										if (chipValue === attemptsValue?.from) {
											handleFilterChange(name, { ...attemptsValue, from: undefined }, type);
										} else if (chipValue === attemptsValue?.to) {
											handleFilterChange(name, { ...attemptsValue, to: undefined }, type);
										}
									} else {
										handleFilterChange(
											name,
											Array.isArray(value)
												? value.filter(
														(v): v is Option => typeof v === "object" && "value" in v && v.value !== chipValue
													)
												: (value as Option[] | Option | string),
											type
										);
									}
								}}
							/>
						);
					})}

					{hasFilters ? <Button onClick={handleClearFilters}>Clear filters</Button> : null}
				</Stack>
				{((selectedRowActions && selectedRows?.length > 0) || isAllDataSelected) && (
					<Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
						<Typography color="text.secondary" variant="body2">
							{isAllDataSelected ? "All rows" : ""}
							{isAllDataSelected ? ` (${totalItems})` : ` ${selectedRows.length}`} selected
						</Typography>

						<Dropdown>
							<DropdownTrigger>
								<Button startIcon={<ListIcon />} variant="contained" color="primary">
									Bulk Actions
								</Button>
							</DropdownTrigger>
							<DropdownPopover>
								<DropdownActionsList
									actions={selectedRowActions || []}
									selectedRows={selectedRows}
									isAllDataSelected={isAllDataSelected}
								/>
							</DropdownPopover>
						</Dropdown>
					</Stack>
				)}
				{filtersDrawerOptions.length > 0 && (
					<FiltersDrawer
						filters={drawerFilters}
						setFilters={setDrawerFilters}
						filtersOptions={filtersDrawerOptions}
						isFilterDrawerDisabled={isFilterDrawerDisabled}
					/>
				)}
				{availableFields.length > 0 && (
					<AdvancedFilterButton
						conditions={conditions}
						setConditions={setConditions}
						availableFields={availableFields}
						disabled={isFilterDrawerDisabled}
					/>
				)}
				{renderHeaderButtons && renderHeaderButtons()}
			</Stack>
		</Stack>
	);
}

function DropdownActionsList({
	actions,
	selectedRows,
	isAllDataSelected,
}: {
	actions: any[];
	selectedRows: string[];
	isAllDataSelected?: boolean;
}) {
	const { onClose } = React.useContext(DropdownContext);

	return (
		<List>
			{actions.map((action) => (
				<ListItem key={action.label} disablePadding>
					<ListItemButton
						onClick={() => {
							action.onClick(selectedRows, isAllDataSelected);
							onClose();
						}}
						dense
						sx={{
							py: 1,
							gap: 1,
							alignItems: "center",
							"& .MuiListItemText-primary": { fontSize: "0.875rem" },
						}}
					>
						<ListItemText
							primary={action.label}
							sx={{
								"& .MuiListItemText-primary": { fontSize: "0.875rem" },
							}}
						/>
					</ListItemButton>
				</ListItem>
			))}
		</List>
	);
}

function TextPopover({
	title,
	type,
	filterName,
	options,
	filter,
}: {
	title: string;
	type: FilterType;
	filterName: string | null;
	options?: Array<Option>;
	filter?: FilterOption;
}): React.JSX.Element {
	const { anchorEl, onApply, onClose, open, value: initialValue } = useFilterContext();
	const [value, setValue] = React.useState<any>(initialValue as any);

	React.useEffect(() => {
		setValue(initialValue || (type === FilterType.ATTEMPTS ? { from: undefined, to: undefined } : ""));
	}, [initialValue, type]);

	return (
		<FilterPopover anchorEl={anchorEl} onClose={onClose} open={open} title={title}>
			<FormControl>
				{type === FilterType.ATTEMPTS && (
					<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
						<TextField
							placeholder="From"
							type="number"
							value={(value as { from?: string; to?: string })?.from || ""}
							onChange={(event) => {
								const currentValue = (value as { from?: string; to?: string }) || {};
								setValue({ ...currentValue, from: event.target.value });
							}}
							sx={{ flex: 1 }}
						/>
						<Box>-</Box>
						<TextField
							placeholder="To"
							type="number"
							value={(value as { from?: string; to?: string })?.to || ""}
							onChange={(event) => {
								const currentValue = (value as { from?: string; to?: string }) || {};
								setValue({ ...currentValue, to: event.target.value });
							}}
							sx={{ flex: 1 }}
						/>
					</Box>
				)}
				{type === FilterType.TEXT && (
					<OutlinedInput
						onChange={(event) => {
							setValue(event.target.value);
						}}
						onKeyUp={(event) => {
							if (event.key === "Enter") {
								onApply(value);
							}
						}}
						value={value}
						placeholder={(filterName?.[0]?.toUpperCase() || "") + filterName?.slice(1)}
					/>
				)}
				{type === FilterType.DATE && (
					<CustomDatePicker
						label={title}
						value={value}
						setValue={(date) => {
							setValue(date as string);
						}}
						fieldStyles={{ width: "100%" }}
					/>
				)}
				{type === FilterType.TIMESTAMP && (
					<CustomTimePicker
						label={""}
						value={value}
						setValue={(time) => {
							setValue(time as string);
						}}
						fieldStyles={{ width: "100%" }}
					/>
				)}
				{type === FilterType.MULTI_SELECT && (
					<ChipsAutocomplete
						placeholder={`Select ${title}`}
						options={options || []}
						value={value}
						setValue={(value) => {
							setValue(value);
						}}
					/>
				)}
				{type === FilterType.SELECT && (
					<FilterSelect
						label={title}
						value={value}
						onChange={(value) => {
							setValue(value);
						}}
						placeholder={`Select ${title}`}
						options={options || []}
					/>
				)}
				{type === FilterType.FETCH_MULTI_SELECT && (
					<FilterFetchMultiSelect
						label={title}
						value={value}
						onChange={(value) => {
							setValue(value);
						}}
						placeholder={`Select ${title}`}
						getOptionsUrl={filter?.getOptionsUrl || ""}
						fetchLabel={filter?.fetchLabel || ""}
						fetchValue={filter?.fetchValue || ""}
					/>
				)}
				{type === FilterType.MULTI_SELECT_FREE_SOLO && (
					<ChipsFreeSolo
						placeholder={`Enter ${title}`}
						value={value}
						setValue={(value) => {
							setValue(value);
						}}
					/>
				)}
				{type === FilterType.FETCH_SELECT && (
					<FilterFetchSelect
						label={title}
						value={value}
						onChange={(value) => {
							setValue(value);
						}}
						placeholder={`Select ${title}`}
						getOptionsUrl={filter?.getOptionsUrl || ""}
						fetchLabel={filter?.fetchLabel || ""}
						fetchValue={filter?.fetchValue || ""}
						withoutFetchPagination={filter?.withoutFetchPagination}
					/>
				)}
			</FormControl>
			<Button
				onClick={() => {
					onApply(value);
				}}
				variant="contained"
			>
				Apply
			</Button>
		</FilterPopover>
	);
}
